import {
  CatalogCellType,
  ColSourceGroup,
  ColumnCatalogEntry,
  HELIOS_GROUP,
} from '../models/source-definition.model'
import {
  ColumnGroupInfo,
  ColumnGroupKey,
  ColumnType,
  RpsSource,
  SourceFieldCatalog,
} from '../models/field-catalog.model'

/**
 * Pure adapter mapping the Flex Transactions field-catalog API shape onto the internal
 * {@link ColumnCatalogEntry} shape the ColDef factory already consumes. This is the ONLY seam
 * between the API and the tested rendering logic in col-def-factory.ts — that file (and its
 * `catalogColDef`) stay unchanged.
 *
 * The API differs from the internal shape on four axes, each concentrated here:
 *  - type (value semantics) vs cellType (rendering hint): TEXT/DATE/DATETIME/BOOL map straight;
 *    NUM maps to `number` and is upgraded to money at RUNTIME by the value-shape-driven money
 *    formatter (see renderers.isMoney) — the API has no MONEY type.
 *  - colSource group key vs the internal display-label union: resolved via the server-provided
 *    ColumnGroupInfo.label, preserving the server's top-to-bottom group order.
 *  - visibleByDefault vs hide: hide = !visibleByDefault.
 *  - the API returns no width: a type-driven default is supplied here.
 */

/**
 * Thrown when a `fields` response carries a column with no boolean `isDynamic`.
 *
 * `isDynamic` is the ROUTING contract (PVAL-26129 §3), and it is non-optional on the DTO. Defaulting
 * an absent flag is the one thing this adapter must not do: default it to `false` and every dynamic
 * key silently routes to `filter.<field>`, where the backend ignores it
 * (`FAIL_ON_UNKNOWN_PROPERTIES` is off) and returns UNFILTERED rows with no error — a grid that
 * quietly answers the wrong question, and the failure mode the acceptance criteria cannot detect from
 * the inside. Default it to `true` and every static name lands in `dynamicFilters`, which 400s.
 *
 * So it throws instead, and the throw is deliberately raised inside the catalog fetch's `map` so it
 * lands in the RETRYABLE columns-error state the component already has (`fetchCatalog` classifies a
 * non-HTTP failure as retryable). A visible, retryable error beats an invisibly all-static grid.
 */
export class FieldCatalogContractError extends Error {
  constructor(readonly source: RpsSource, readonly fields: string[]) {
    super(
      `Field catalog for ${source} is missing the isDynamic routing flag on ${fields.length} ` +
        `column(s): ${fields.join(', ')}`,
    )
    this.name = 'FieldCatalogContractError'
    // Restores the prototype chain, which subclassing a built-in loses when downlevelled — without
    // it `instanceof FieldCatalogContractError` is false at runtime.
    Object.setPrototypeOf(this, FieldCatalogContractError.prototype)
  }
}

/** Default column width per value type — the API returns none, so the UI owns this. */
export const WIDTH_BY_TYPE: Record<ColumnType, number> = {
  TEXT: 140,
  NUM: 120,
  DATE: 120,
  DATETIME: 170,
  BOOL: 100,
  TEXT_LIST: 180,
}

/**
 * Per-field width overrides for the handful of Helios columns whose ideal width the type default
 * misses. Keyed on the canonical `field`.
 */
export const WIDTH_BY_FIELD: Record<string, number> = {}

/** Map an API value type to the internal cell-rendering type. NUM stays `number`; money is a
 * runtime value-shape upgrade, never type-driven (contract Decision 3). */
const cellTypeOf = (type: ColumnType): CatalogCellType => {
  switch (type) {
    case 'DATE':
      return 'date'
    case 'DATETIME':
      return 'datetime'
    case 'BOOL':
      return 'boolean'
    case 'NUM':
      return 'number'
    case 'TEXT_LIST':
      return 'list'
    case 'TEXT':
    default:
      return 'text'
  }
}

/**
 * Fallback key→internal-group-label map, used only when the server omits a group's label. The
 * server-provided ColumnGroupInfo.label is authoritative when present.
 */
const GROUP_LABEL_BY_KEY: Record<ColumnGroupKey, ColSourceGroup> = {
  HELIOS: 'Helios',
  DEFAULT: 'Default',
  SECURITY_MASTER: 'Sec Master',
  TRADES: 'Trades',
  CASH_ACTIVITY: 'Cash Activity',
  AGGREGATE_INFO: 'Aggregate Info',
}

/** Build a colSource key -> display label lookup from the catalog's groups. */
const groupLabelLookup = (
  groups: ColumnGroupInfo[],
): Record<string, string> => {
  const lookup: Record<string, string> = {}
  groups.forEach(group => {
    lookup[group.key] = group.label
  })
  return lookup
}

/**
 * Title-case a group token with no label anywhere: `SOME_GROUP` -> `Some Group`,
 * `someGroup` -> `Some Group`. Both shapes are handled because the backend has emitted camelCase
 * group keys (`aggregateInformation`) alongside the screaming-snake ones.
 */
export const titleCaseGroupToken = (token: string): string =>
  token
    // Split on underscores and on camelCase / ACRONYMWord boundaries alike.
    .replace(/_+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

/**
 * Resolve a column's group label: the server's own label first, then the fallback map for a key it
 * declared no group for, and finally the key itself made readable.
 *
 * The last step matters. Returning `undefined` here used to send the column to the Column Manager's
 * ungrouped bucket, so a group key the server added but did not list in `groups[]` would silently
 * pile up under "Other". Falling back to the key keeps the column filed under the source the API
 * actually gave it.
 */
const groupLabelOf = (
  colSource: ColumnGroupKey,
  labels: Record<string, string>,
): ColSourceGroup =>
  (labels[colSource] ??
    GROUP_LABEL_BY_KEY[colSource] ??
    titleCaseGroupToken(colSource)) as ColSourceGroup

/**
 * Convert one API {@link SourceFieldCatalog} into the ordered internal catalog entries. Column
 * order is preserved verbatim (the API already returns them in grid order).
 */
/**
 * Ordered group display labels for one catalog, used by the Column Manager to lay its sections out
 * top to bottom. The server's group order is authoritative and is never sorted.
 *
 * `Helios` is forced to the front: the UI's computed leading columns live in that group but are not
 * catalog entries, so a catalog whose groups omit HELIOS would otherwise drop the section entirely.
 * Filtering before prepending keeps the group from appearing twice when a catalog does return it.
 *
 * As of PVAL-26129 this is load-bearing rather than defensive: **neither per-source endpoint returns
 * a HELIOS group at all.** Breaks / Source / Status / the match-count columns are no longer
 * advertised by either one, so the section exists only because it is prepended here and the ColDef
 * factory attributes catalog-less computed columns to it.
 */
export function groupOrderFromApi(catalog: SourceFieldCatalog): string[] {
  const serverOrder = catalog.groups.map(
    group => group.label || GROUP_LABEL_BY_KEY[group.key],
  )
  return [HELIOS_GROUP, ...serverOrder.filter(label => label !== HELIOS_GROUP)]
}

/** One catalog adapted for the grid: ordered columns plus the Column Manager's section order. */
export interface AdaptedCatalog {
  columns: ColumnCatalogEntry[]
  /** Group labels in server display order. */
  groupOrder: string[]
}

/** Adapt an API catalog into everything the grid needs from it. */
export function adaptCatalog(catalog: SourceFieldCatalog): AdaptedCatalog {
  return {
    columns: catalogFromApi(catalog),
    groupOrder: groupOrderFromApi(catalog),
  }
}

export function catalogFromApi(
  catalog: SourceFieldCatalog,
): ColumnCatalogEntry[] {
  const labels = groupLabelLookup(catalog.groups)
  const uncontracted = catalog.columns
    .filter(descriptor => typeof descriptor.isDynamic !== 'boolean')
    .map(descriptor => descriptor.field)
  if (uncontracted.length > 0) {
    throw new FieldCatalogContractError(catalog.source, uncontracted)
  }
  return catalog.columns.map(descriptor => ({
    field: descriptor.field,
    headerName: descriptor.label,
    colSource: groupLabelOf(descriptor.colSource, labels),
    cellType: cellTypeOf(descriptor.type),
    // Carried through verbatim — this is the filter/sort routing contract (PVAL-26129 §3) and the
    // adapter is the only place it can enter the UI. It is non-optional on the DTO and validated
    // above rather than coerced: see {@link FieldCatalogContractError} for why a missing flag has to
    // fail loudly instead of defaulting either way.
    isDynamic: descriptor.isDynamic,
    // The catalog type, unmapped. `cellType` above is the lossy rendering hint; the request builder
    // needs the real type to pick a sort `type` / filter subtype.
    valueType: descriptor.type,
    // A TEXT_LIST column is hidden by default whatever the backend says: the match id lists are raw
    // internal ids, and the backend sends them visibleByDefault deliberately, leaving the call to the
    // UI. They stay one click away in the Column Manager.
    hide: !descriptor.visibleByDefault || descriptor.type === 'TEXT_LIST',
    width: WIDTH_BY_FIELD[descriptor.field] ?? WIDTH_BY_TYPE[descriptor.type],
  }))
}
